import type { ReactNode } from 'react';
import {
    LOWER_PERMANENT,
    LOWER_PRIMARY,
    UPPER_PERMANENT,
    UPPER_PRIMARY,
    sortTeeth,
    type GridTooth
} from './toothNumbering';

type Cell = GridTooth | null;

/** Primary teeth sit under permanent 4-13 (upper) and 20-29 (lower): 3 empty cells each side. */
function padPrimary(teeth: readonly GridTooth[]): Cell[] {
    return [null, null, null, ...teeth, null, null, null];
}

const UPPER_ISO = [...UPPER_PERMANENT, ...UPPER_PRIMARY].map((tooth) => tooth.iso);
const LOWER_ISO = [...LOWER_PERMANENT, ...LOWER_PRIMARY].map((tooth) => tooth.iso);

interface ToothGridProps {
    selected: readonly string[];
    onChange: (teeth: string[]) => void;
}

// Universal numbers on screen, ISO values stored. Patient right on the left, as DM draws it.
export function ToothGrid({ selected, onChange }: ToothGridProps) {
    const chosen = new Set(selected);
    const commit = (next: Set<string>) => onChange(sortTeeth([...next]));
    const toggle = (iso: string) => {
        const next = new Set(chosen);
        if (next.has(iso)) next.delete(iso);
        else next.add(iso);
        commit(next);
    };
    const setJaw = (teeth: readonly string[], on: boolean) => {
        const next = new Set(chosen);
        teeth.forEach((iso) => (on ? next.add(iso) : next.delete(iso)));
        commit(next);
    };

    const cell = (tooth: Cell, index: number, primary: boolean) => (
        <div key={index} className="flex h-7 w-7 items-center justify-center">
            {tooth && (
                <button
                    type="button"
                    onClick={() => toggle(tooth.iso)}
                    aria-pressed={chosen.has(tooth.iso)}
                    aria-label={`Tooth ${tooth.label}`}
                    className={`flex items-center justify-center font-medium tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                        primary
                            ? 'h-[22px] w-[22px] rounded-full text-[10px]'
                            : 'h-[26px] w-[26px] rounded-md text-[11px]'
                    } ${
                        chosen.has(tooth.iso)
                            ? 'border border-[#007E87] bg-[#007E87] text-white'
                            : primary
                              ? 'border border-dashed border-gray-300 text-gray-500 hover:border-gray-400'
                              : 'border border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                >
                    {tooth.label}
                </button>
            )}
        </div>
    );
    const row = (cells: readonly Cell[], primary = false) => (
        <div className="flex">
            {cells.slice(0, 8).map((tooth, index) => cell(tooth, index, primary))}
            <div className="mx-1.5 w-px bg-gray-200" />
            {cells.slice(8).map((tooth, index) => cell(tooth, index + 8, primary))}
        </div>
    );
    const jaw = (label: string, rows: ReactNode, teeth: readonly string[]) => (
        <div className="flex items-center gap-3">
            <div className="w-14 shrink-0">
                <div className="text-xs font-semibold text-gray-700">{label}</div>
                <div className="mt-0.5 flex gap-2 text-xs">
                    <button
                        type="button"
                        className="text-[#007E87] hover:underline"
                        onClick={() => setJaw(teeth, true)}
                    >
                        All
                    </button>
                    <button
                        type="button"
                        className="text-gray-500 hover:underline"
                        onClick={() => setJaw(teeth, false)}
                    >
                        None
                    </button>
                </div>
            </div>
            <div>{rows}</div>
        </div>
    );

    return (
        <div className="inline-flex flex-col">
            <div className="mb-1 ml-[68px] flex justify-between text-[10px] uppercase tracking-wide text-gray-400">
                <span>Patient right</span>
                <span>Patient left</span>
            </div>
            {jaw(
                'Upper',
                <>
                    {row(UPPER_PERMANENT)}
                    {row(padPrimary(UPPER_PRIMARY), true)}
                </>,
                UPPER_ISO
            )}
            <div className="my-1.5 ml-[68px] h-px bg-gray-200" />
            {jaw(
                'Lower',
                <>
                    {row(padPrimary(LOWER_PRIMARY), true)}
                    {row(LOWER_PERMANENT)}
                </>,
                LOWER_ISO
            )}
        </div>
    );
}
